import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@material-ui/core";
import { ChevronRight } from "@material-ui/icons";
import dayjs from "dayjs";
import moment from "moment";
import "moment/locale/pt-br";
import "moment/locale/es";
import useDateFormatter from "../../hooks/useDateFormatter";
import Utils from "~/helpers/Utils";

const P = "#8C1837";

const SectionLabel = ({ label }) => (
	<Typography style={{
		fontSize: 11,
		fontWeight: 700,
		color: "#999",
		letterSpacing: 1.4,
		textTransform: "uppercase",
		padding: "16px 16px 8px",
	}}>
		{label}
	</Typography>
);

const getCardDate = (startTime, timeZone, locale) => {
	if (!startTime) return null;

	const date = dayjs(startTime).tz(timeZone);
	const m = moment(date.valueOf()).locale(locale);

	const hours = parseInt(m.format("H"), 10);
	const minutes = parseInt(m.format("mm"), 10);

	let timeStr;
	if (locale === "en") {
		timeStr = m.format(minutes === 0 ? "ha" : "h:mma").toLowerCase();
	} else {
		timeStr = minutes === 0 ? `${hours}h` : `${hours}h${String(minutes).padStart(2, "0")}`;
	}

	let dateStr;
	if (locale === "en") {
		const weekday = m.format("dddd");
		const day = m.format("D");
		const month = m.format("MMMM");
		dateStr = `${weekday}, ${month} ${day}`;
	} else {
		let weekday = m.format("dddd");
		weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
		weekday = weekday.replace("-feira", "");
		const day = m.format("D");
		const month = m.format("MMMM");
		dateStr = `${weekday}, ${day} de ${month}`;
	}

	return `${dateStr} · ${timeStr}`;
};

const ReservationCard = ({ item, isPast, onClickCard, bookingPayment, currency, t, locale }) => {
	const isSessionPackage = !!item?.packageId;
	const pricingTypeName = item?.pricingType?.name;
	const isUponConsultation = Utils?.dontShowPricingTypeNames?.includes(pricingTypeName);
	const paid = bookingPayment(item.bookingTransactionId);
	const isConfirmed = item.status === "active" && item.confirmReservation;
	const isUpcoming = !isPast;

	const cardDate = getCardDate(item.startTime, item?.business?.timeZone, locale);

	// ── Payment / package info ──
	let paymentLine = null;
	if (isSessionPackage) {
		paymentLine = t("reservas_session_package_included");
	} else if (!isUponConsultation && item.price) {
		const price = item.price;
		const remaining = price - paid;
		if (paid > 0 && remaining > 0) {
			paymentLine = `${Utils.formatMoney(remaining / 100, currency)} ${t("reservas_pay_at_location")} · ${Utils.formatMoney(paid / 100, currency)} ${t("reservas_already_paid")}`;
		} else if (paid > 0 && remaining <= 0) {
			paymentLine = `${Utils.formatMoney(paid / 100, currency)} ${t("Paid")}`;
		} else {
			paymentLine = `${Utils.formatMoney(price / 100, currency)} ${t("reservas_pay_at_location")}`;
		}
	}

	// ── Status / action ──
	// Regra: "Confirmar presença" só aparece se o agendamento for nas próximas 24h
	// (mesma lógica de isSameDayBooking em AppointmentSummaryScreen)
	const diffDays = item.startTime ? dayjs(item.startTime).diff(dayjs(), "day", true) : -1;
	const isWithin24h = diffDays > 0 && diffDays < 1;

	let statusText = null;
	let statusIsConfirmed = false;
	let statusIsCancelled = false;
	let statusIsInactive = false;
	if (item.status?.toLowerCase() === "cancelled") {
		statusText = t("cancelled");
		statusIsCancelled = true;
	}  else if (item.status?.toLowerCase() === "inactive") {
		statusText = t("inactive");
		statusIsInactive = true;
	} else if (isUpcoming) {
		if (isConfirmed) {
			statusText = t("reservas_confirmed_presence");
			statusIsConfirmed = true;
		} else if ((item.status === "active" || item.status === "pending_accept") && isWithin24h) {
			statusText = t("reservas_confirm_presence");
		} else if (item.status === "active" || item.status === "pending_accept") {
			statusText = t("reservas_scheduled");
		}
	}

	return (
		<div
			onClick={onClickCard}
			style={{
				backgroundColor: "#fff",
				borderRadius: 12,
				border: "1px solid #EDEDE6",
				borderLeft: isConfirmed ? `4px solid ${P}` : "1px solid #EDEDE6",
				padding: "14px 16px",
				cursor: "pointer",
				display: "flex",
				alignItems: "flex-start",
				gap: 8,
			}}>
			<div style={{ flex: 1, minWidth: 0 }}>
				{/* Date · Time */}
				<Typography style={{ fontSize: 16, fontWeight: 700, color: "#1A1A1A", marginBottom: 2, lineHeight: 1.3 }}>
					{cardDate || t("To be scheduled")}
				</Typography>

				{/* Service name */}
				<Typography translate="no" style={{ fontSize: 14, fontWeight: 700, color: "#1A1A1A", marginBottom: 2, lineHeight: 1.3 }}>
					{item?.name}
				</Typography>

				{/* Business · com Professional */}
				<Typography translate="no" style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>
					{item?.business?.name}{item?.professional ? ` · com ${item.professional}` : ""}
				</Typography>

				{/* Payment or package info */}
				{(paymentLine && !statusIsCancelled) && (
					<Typography style={{ fontSize: 13, color: "#555", marginBottom: statusText ? 8 : 0 }}>
						{paymentLine}
					</Typography>
				)}

				{/* Status / action */}
				{statusText && (
					<Typography style={{
						fontSize: 14,
						fontWeight: 700,
						color: statusIsConfirmed ? "#2B7A2B"
							: (statusIsCancelled || statusIsInactive) ? "#9E9E9E"
							: statusText === t("reservas_confirm_presence") ? P
							: statusText === t("reservas_scheduled") ? "#1A6FA8"
							: "#666",
					}}>
						{statusText === t("reservas_confirm_presence") ? `${statusText} ›` : statusText}
					</Typography>
				)}
			</div>

			<ChevronRight style={{ color: "#C4C4C4", fontSize: 22, flexShrink: 0, marginTop: 2 }} />
		</div>
	);
};

const ListReservations = (props) => {
	const {
		reservations,
		handleClickOpen,
		bookingPayment,
		loadMoreReservations,
		business,
		isUpcomingMode,
		upcomingTotalCount,
		onViewAll,
	} = props;

	const { t, i18n } = useTranslation();
	const currency = business?.currency || "BRL";
	const now = dayjs();

	const localeMap = { "pt-BR": "pt-br", "en-US": "en", "es-ES": "es" };
	const locale = localeMap[i18n.language] || "pt-br";

	const upcoming = [...reservations]
		.filter((item) => item.startTime && dayjs(item.startTime).isAfter(now) && !["cancelled", "inactive"].includes(item.status?.toLowerCase()))
		.sort((a, b) => dayjs(a.startTime) - dayjs(b.startTime));

	const past = [...reservations]
		.filter((item) => !item.startTime || !dayjs(item.startTime).isAfter(now) || ["cancelled", "inactive"].includes(item.status?.toLowerCase()))
		.sort((a, b) => {
			const dateA = a.startTime ? dayjs(a.startTime) : dayjs(0);
			const dateB = b.startTime ? dayjs(b.startTime) : dayjs(0);
			return dateB - dateA;
		});

	const renderCard = (item, index, isPast) => (
		<ReservationCard
			key={`reservation-${item.bookingId || index}`}
			item={item}
			isPast={isPast}
			onClickCard={() => handleClickOpen(item)}
			bookingPayment={bookingPayment}
			currency={currency}
			t={t}
			locale={locale}
		/>
	);

	return (
		<div style={{ backgroundColor: "#F6F6F3", paddingBottom: 60, minHeight: "calc(100vh - 200px)" }}>
			{upcoming.length > 0 && (
				<div>
					<SectionLabel label={t("reservas_upcoming")} />
					<div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 16px" }}>
						{upcoming.map((item, i) => renderCard(item, i, false))}
					</div>
				</div>
			)}

			{past.length > 0 && (
				<div>
					<SectionLabel label={t("reservas_past")} />
					<div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 16px" }}>
						{past.map((item, i) => renderCard(item, upcoming.length + i, true))}
					</div>
				</div>
			)}

			{isUpcomingMode && onViewAll ? (
				<div style={{ padding: "16px", textAlign: "center", marginBottom: 50 }}>
					<Typography
						onClick={onViewAll}
						style={{
							color: "#8C1837",
							fontWeight: 700,
							fontSize: 14,
							cursor: "pointer",
							display: "inline",
						}}
					>
						{upcomingTotalCount != null
							? `${t("reservas_view_all")} (${upcomingTotalCount}) ›`
							: `${t("reservas_view_all")} ›`}
					</Typography>
				</div>
			) : loadMoreReservations && (
				<div style={{ marginBottom: 50 }}>{loadMoreReservations()}</div>
			)}
		</div>
	);
};

export default ListReservations;
